import { useEffect, useRef, useState, type RefObject } from 'react';
import { z } from 'zod';
import { AnalyzerEfolderDialog } from './AnalyzerEfolderDialog';

const requestSchema = z.object({
  type: z.literal('bsa:efolder:open'),
  requestId: z.string().uuid(),
  loanId: z.string().uuid(),
  maxFiles: z.number().int().min(1).max(24)
});
type EfolderRequest = z.infer<typeof requestSchema>;

/** Only the mounted same-origin analyzer can request documents; all reads still use RLS. */
export function AnalyzerEfolderBridge({ frameRef }: { frameRef: RefObject<HTMLIFrameElement> }) {
  const [request, setRequest] = useState<EfolderRequest | null>(null);
  const pending = useRef<EfolderRequest | null>(null);
  const completed = useRef<{ request: EfolderRequest; files: File[] } | null>(null);

  useEffect(() => {
    const frame = frameRef.current;
    const reset = () => {
      completed.current = null;
      if (pending.current) {
        pending.current = null;
        setRequest(null);
      }
    };
    const receive = (event: MessageEvent) => {
      if (event.origin !== window.location.origin || !frame?.contentWindow || event.source !== frame.contentWindow) {
        return;
      }
      if (event.data?.type === 'bsa:efolder:cancel' && event.data.requestId === pending.current?.requestId) {
        reset();
        return;
      }
      const result = requestSchema.safeParse(event.data);
      if (!result.success || pending.current) {
        return;
      }
      pending.current = result.data;
      setRequest(result.data);
      frame.contentWindow.postMessage({ ...result.data, type: 'bsa:efolder:opened' }, window.location.origin);
    };
    window.addEventListener('message', receive);
    frame?.addEventListener('load', reset);
    return () => {
      pending.current = null;
      completed.current = null;
      window.removeEventListener('message', receive);
      frame?.removeEventListener('load', reset);
    };
  }, [frameRef]);

  const finish = (files: File[]) => {
    if (!request || pending.current !== request) {
      return;
    }
    completed.current = { request, files };
    pending.current = null;
    setRequest(null);
  };

  const deliver = () => {
    const result = completed.current;
    completed.current = null;
    if (!result) {
      return;
    }
    // Wait for Radix to release its focus trap before the iframe restores its trigger.
    frameRef.current?.contentWindow?.postMessage({
      type: 'bsa:efolder:result', requestId: result.request.requestId,
      loanId: result.request.loanId, files: result.files
    }, window.location.origin);
  };

  return request ? <AnalyzerEfolderDialog key={request.requestId} loanId={request.loanId}
    maxFiles={request.maxFiles} onFiles={finish} onCancel={() => finish([])} onClosed={deliver} /> : null;
}
